import { useDecision } from '@optimizely/react-sdk'
import { Button } from '@sunwebgroup/uds-ui/button'
import { Dialog } from '@sunwebgroup/uds-ui/dialog'
import { Icon, type IconGlyph } from '@sunwebgroup/uds-ui/icon'
import { IndeterminateProgressCircle } from '@sunwebgroup/uds-ui/IndeterminateProgressCircle'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import { differenceInDays } from 'date-fns'
import { Fragment, use, useEffect, useState } from 'react'
import { useIntl } from '@/intl'
import { AlertServiceSidebar } from '@/scopes/common/components/AlertService/index'
import { PriceDetails } from '@/scopes/common/components/PriceDetails'
import { OPTIMIZELY_TESTS_KEYS } from '@/scopes/common/infrastructure/ab-testing/optimizely/types'
import { useAccommodationContext } from '@/scopes/prices/components/AccommodationContext'
import { HighlightedPaymentMethod } from '@/scopes/prices/components/HighlightedPaymentMethod'
import {
  getPromotedPriceSelector,
  isAccommodationAvailableSelector,
  participantsSelector,
  pricesResponseSelector,
} from '@/scopes/prices/components/selectors'
import UrgencyCampaign from '@/scopes/prices/components/UrgencyCampaign'
import { PricesActionsContext } from '@/scopes/prices/domain/PricesActionsContext'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import styles from './PromotedPrice.module.css'

interface PromotedPriceProps {
  onClick?: () => void
}

const ICON_MAP: Record<string, IconGlyph> = {
  'flight': 'AirplaneToRight',
  'accommodation': 'Bed',
  'skipass': 'SkiPass',
  'skimaterial': 'Skis',
  'car': 'CarRight',
  'ski-material': 'Skis',
  'bus': 'BusRight',
  'transfer': 'BusRight',
  'carrental': 'CarRight',
}

function PromotedPrice({ onClick = () => {} }: PromotedPriceProps) {
  const intl = useIntl()
  const { initPriceTable } = use(PricesActionsContext)
  const { data, isError, loading } = usePricesSelector(getPromotedPriceSelector)
  const { prices } = usePricesSelector(pricesResponseSelector)
  const { isAccommodationAvailable, isFetching } = usePricesSelector(isAccommodationAvailableSelector)
  const { contextItemId, alertServiceDataSourceId } = useAccommodationContext()

  const [{ enabled: useFollowUpSplitPackageParts }] = useDecision(OPTIMIZELY_TESTS_KEYS.FOLLOW_UP_SPLIT_PACKAGES)

  const onClickHandler = () => {
    if (prices.length === 0) {
      initPriceTable()
    }
    onClick()
  }

  if (!isAccommodationAvailable && !isFetching) {
    return (
      <AlertServiceSidebar
        contextItemId={contextItemId}
        alertServiceDataSourceId={alertServiceDataSourceId}
        showBorder
      />
    )
  }

  if (isError) {
    return <PromotedPriceSkeleton error onClick={onClickHandler} />
  }

  if (!data || loading) {
    return <PromotedPriceSkeleton onClick={onClickHandler} />
  }

  const {
    filters,
    departureAirport,
    departureDate,
    paymentMethod,
    campaignCountDown,
    services,
    price,
    campaignId,
    hasExternalFlight,
  } = data

  const departureDateInLessThanSixWeeks = differenceInDays(departureDate, new Date()) < 42
  const shouldShowPaymentMethod = !hasExternalFlight && !departureDateInLessThanSixWeeks
  const formattedDepartureDate = intl.formatDate(departureDate, { weekday: 'short', day: 'numeric', month: 'short' })

  return (
    <>
      <div>
        <div className={styles.wrapper} data-qa="promoted-price">
          <UrgencyCampaign
            price={price}
            campaignId={campaignId}
            campaignCountdown={campaignCountDown?.deadline}
            campaignCountdownText={campaignCountDown?.text}
          >
            {campaignCountDown?.text}
          </UrgencyCampaign>
          <div className={styles.infoWrapper}>
            <div className={styles.services}>
              {services.map((service) => {
                const glyph = ICON_MAP[service]
                return (
                  <Fragment key={service}>
                    {glyph && <Icon glyph={glyph} />}
                    <Icon className={styles.serviceIconDivider} glyph="PlusExtraSmall" />
                  </Fragment>
                )
              })}
            </div>
            <div className={styles.infoExtraWrapper}>
              <Typography variant="body-s-regular-tight">{formattedDepartureDate}</Typography>
              <Typography variant="body-s-regular-tight">{filters.join(', ')}</Typography>
              {departureAirport && (
                <div className={styles.route}>
                  <Icon glyph="AirplaneTakingOff" size={16} />
                  <Typography as="div" variant="body-s-regular-tight">
                    {departureAirport}
                  </Typography>
                </div>
              )}
            </div>
          </div>
          <PriceInfoBox />
          <div className={styles.footerWrapper}>
            <PromotedPriceButton className={styles.footerButton} onClick={onClickHandler} />
            {paymentMethod?.text && paymentMethod?.icon && shouldShowPaymentMethod && (
              <div className={styles.highlightedPaymentMethodWrapper}>
                <HighlightedPaymentMethod text={paymentMethod?.text} icon={paymentMethod?.icon} />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={styles.stickyBar}>
        <div className={cn(styles.stickyBarContent, useFollowUpSplitPackageParts && styles.stickyBarContentTinted)}>
          {useFollowUpSplitPackageParts
            ? (
                <>
                  <div className={styles.stickyBarColumnLeft}>
                    <div className={styles.infoExtraStickyWrapper}>
                      <Typography variant="body-s-regular-tight">{formattedDepartureDate}</Typography>
                      <Typography variant="body-s-regular-tight" aria-hidden>
                        |
                      </Typography>
                      <Typography variant="body-s-regular-tight">{filters[0]}</Typography>
                    </div>
                    <PriceInfoBox sticky />
                  </div>

                  <div className={styles.stickyBarColumnRight}>
                    <PromotedPriceButton className={styles.stickyBarButton} onClick={onClickHandler} />
                  </div>
                </>
              )
            : (
                <>
                  <PriceInfoBox sticky />
                  <PromotedPriceButton className={styles.stickyBarButton} onClick={onClickHandler} />
                </>
              )}
        </div>
      </div>
    </>
  )
}

function PriceInfoBox({ sticky }: { sticky?: boolean }) {
  const intl = useIntl()
  const { data } = usePricesSelector(getPromotedPriceSelector)
  const { selectedValues: participants } = usePricesSelector(participantsSelector)

  if (!data) {
    return null
  }

  const { price, transportType, legacyDepartureDate, mealplan, accommodationId, duration } = data

  const priceOverviewTitle = sticky
    ? intl.formatMessage({ id: 'price_overview_abbreviation' })
    : intl.formatMessage({ id: 'price_box_price_text' }, { isPerPerson: true })
  const originalPriceWithCurrency = price.originalAveragePrice
    ? intl.formatCurrency(price.originalAveragePrice)
    : null
  const priceWithCurrency = intl.formatTruncatedCurrency(price.averagePrice)

  const absoluteDiscountValue = price.originalAveragePrice - price.averagePrice
  const discountLabel = `-${intl.formatCurrency(absoluteDiscountValue)}`

  return (
    <div className={cn(styles.priceInfoBoxWrapper, !sticky && styles.priceInfoBoxWrapperNotSticky)}>
      {!sticky && (
        <div className={styles.discountSection}>
          {originalPriceWithCurrency && (
            <Typography className={styles.originalPriceWithCurrency} variant="body-s-regular-flat">
              {originalPriceWithCurrency}
            </Typography>
          )}
          {price.discountPercentage && (
            <Tag variant="primary-tinted" size="M">
              {discountLabel}
            </Tag>
          )}
        </div>
      )}
      <div className={cn(sticky && styles.priceSectionSticky)}>
        <Typography
          className={cn(styles.price, sticky && styles.priceSticky)}
          data-qa-price-value={priceWithCurrency}
          variant="display-c-regular"
        >
          {priceWithCurrency}
        </Typography>
        <div className={styles.priceDetailsSection}>
          <Typography className={styles.priceOverviewTitle} variant="body-s-regular-tight">
            {priceOverviewTitle}
          </Typography>
          <PriceDetails
            priceDetailsQueryParams={{
              transportType,
              accommodationId,
              duration,
              mealPlan: mealplan,
              departureDate: legacyDepartureDate.date,
              participants,
            }}
          />
        </div>
      </div>
    </div>
  )
}

function PromotedPriceSkeleton({ error = false, onClick }: { error?: boolean, onClick: () => void }) {
  const intl = useIntl()
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true)
    }, 1000)
    return () => clearTimeout(timer)
  }, [])

  return (
    <>
      <div className={styles.skeletonWrapper}>
        {error && (
          <div className={styles.errorContentWrapper}>
            <Dialog
              text={intl.formatMessage({ id: 'prices_promoted-price-error-title' })}
              supportingText={intl.formatMessage({ id: 'prices_promoted-price-error-message' })}
              type="alert"
            />
            <PromotedPriceButton className={styles.errorButton} onClick={onClick} />
          </div>
        )}
        {!error && (
          <>
            <div className={cn(styles.skeletonBody, loading && styles.disabled)}>
              <div className={styles.skeletonPrice}>
                <div className={styles.priceText}>
                  {loading && <IndeterminateProgressCircle className={styles.skeletonProgressCircle} />}
                  <Typography variant="display-c-regular">{intl.formatCurrency(0, { showSign: true })[0]}</Typography>
                </div>
                <div className={styles.skeletonPriceInfoBox}>
                  <Typography variant="body-s-regular-tight">
                    {intl.formatMessage({ id: 'price_box_price_text' }, { isPerPerson: true })}
                  </Typography>
                  <Icon glyph="InformationInCircle" size={16} />
                </div>
              </div>
            </div>
            <PromotedPriceButton className={styles.skeletonDesktopButton} onClick={onClick} />
            {' '}
          </>
        )}
      </div>
      <div className={styles.stickyBar}>
        <div className={styles.skeletonStickyBarBody}>
          {!error && (
            <div className={cn(styles.skeletonPrice, loading && styles.disabled)}>
              {loading && !error && (
                <IndeterminateProgressCircle
                  className={cn(styles.skeletonProgressCircle, loading && styles.disabled)}
                />
              )}

              <div className={styles.priceText}>
                <Typography variant="display-c-regular">{intl.formatCurrency(0, { showSign: true })[0]}</Typography>
                <Typography variant="body-s-regular-tight">
                  {intl.formatMessage({ id: 'price_overview_abbreviation' })}
                </Typography>
                <Icon glyph="InformationInCircle" size={16} />
              </div>
            </div>
          )}
          <PromotedPriceButton onClick={onClick} />
        </div>
      </div>
    </>
  )
}

interface PromotedPriceButtonProps {
  className?: string
  onClick?: () => void
}

function PromotedPriceButton({ className, onClick }: PromotedPriceButtonProps) {
  const intl = useIntl()

  return (
    <Button className={className} data-qa="promoted-price-btn" onClick={onClick}>
      {intl.formatMessage({ id: 'prices_promoted_price_button-text' })}
    </Button>
  )
}

export default PromotedPrice
